import { useMemo, useState } from 'react';
import { ChevronsUpDownIcon, RotateCcwIcon, UserRoundIcon } from 'lucide-react';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/combobox';
import type { UserSummary } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/shared/components/UserAvatar';

/** The row that returns the gate to the workflow's own assignee. */
const USE_WORKFLOW = '';

/** Case-insensitive match on either half of how a person is known. */
function matches(people: readonly UserSummary[], query: string): UserSummary[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...people];
  return people.filter(
    (person) =>
      person.display_name.toLowerCase().includes(needle) ||
      person.email.toLowerCase().includes(needle),
  );
}

/** Where the gate's person comes from, in a few words under their name. */
function sourceLine(
  isOverride: boolean,
  workflowAssignee: UserSummary | null,
): string {
  if (!isOverride) {
    return workflowAssignee ? 'From the workflow' : 'The workflow names no one';
  }
  return workflowAssignee
    ? `This task only · the workflow names ${workflowAssignee.display_name}`
    : 'This task only · the workflow names no one';
}

/**
 * Who a task's gate routes to, and a way to change it (spec 180).
 *
 * The field shows the answer, not a search box: the person, as the Runs tab
 * shows people (their disc and name, at reading size), and one quiet line
 * saying where that comes from. It used to be an always-empty search field with
 * the choice in a small grey line beneath it and a second line repeating the
 * state, and when the gate followed the workflow it never said whom the
 * workflow named.
 *
 * Pressing it opens the people, searched by name or address. When the task has
 * its own assignee, the first row puts it back on the workflow's, named: that
 * is what clearing an override does (spec 067 FR-032), so the row says so.
 */
export function GateAssigneeField({
  people,
  peopleLoading,
  directoryUnavailable,
  assignee,
  isOverride,
  workflowAssignee,
  disabled,
  onAssign,
  onUseWorkflow,
}: {
  /** Everyone who may be assigned. */
  people: readonly UserSummary[];
  /** The directory has not answered yet; the list says so rather than "no one". */
  peopleLoading: boolean;
  /** The directory could not be read; changes only the no-results text. */
  directoryUnavailable: boolean;
  /** Whom the gate routes this task to now. */
  assignee: UserSummary | null;
  /** Whether that is this task's own choice rather than the workflow's. */
  isOverride: boolean;
  /** Whom the workflow itself names at this gate. */
  workflowAssignee: UserSummary | null;
  disabled: boolean;
  onAssign: (person: UserSummary) => void;
  onUseWorkflow: () => void;
}) {
  const [query, setQuery] = useState('');
  const found = useMemo(() => matches(people, query), [people, query]);
  const source = sourceLine(isOverride, workflowAssignee);
  const items = [
    ...(isOverride ? [USE_WORKFLOW] : []),
    ...found.map((person) => person.id),
  ];

  return (
    <Combobox<string, false>
      value={assignee?.id ?? null}
      onOpenChange={(open) => {
        if (!open) setQuery('');
      }}
      onValueChange={(next) => {
        if (next === USE_WORKFLOW) {
          onUseWorkflow();
          return;
        }
        // Choosing whom the gate already routes to changes nothing; an
        // override naming the workflow's own person would only say "this task
        // only" about a choice nobody made.
        if (next === null || next === assignee?.id) return;
        const person = people.find((candidate) => candidate.id === next);
        if (person) onAssign(person);
      }}
      items={items}
      disabled={disabled}
      itemToStringLabel={(id) =>
        id === USE_WORKFLOW
          ? 'Use the workflow’s'
          : (people.find((person) => person.id === id)?.display_name ?? id)
      }
    >
      <ComboboxTrigger
        aria-label="Assignee for this task"
        aria-description={`${assignee?.display_name ?? 'Unassigned'}. ${source}.`}
        className={cn(
          'border-input flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors',
          'hover:bg-accent/60 focus-visible:ring-ring focus-visible:ring-3 focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        {assignee ? (
          <UserAvatar
            id={assignee.id}
            displayName={assignee.display_name}
            email={assignee.email}
            className="size-7"
          />
        ) : (
          <span
            aria-hidden="true"
            className="border-muted-foreground/50 text-muted-foreground flex size-7 shrink-0 items-center justify-center rounded-full border border-dashed"
          >
            <UserRoundIcon className="size-3.5" strokeWidth={1.75} />
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className={cn(
              'truncate text-sm',
              assignee
                ? 'text-foreground font-medium'
                : 'text-muted-foreground',
            )}
          >
            {assignee?.display_name ?? 'Unassigned'}
          </span>
          <span className="text-muted-foreground truncate text-xs">
            {source}
          </span>
        </span>
        <ChevronsUpDownIcon
          aria-hidden="true"
          className="text-muted-foreground size-4 shrink-0"
          strokeWidth={1.5}
        />
      </ComboboxTrigger>

      <ComboboxContent>
        <ComboboxInputGroup className="mb-1">
          <ComboboxInput
            aria-label="Search people"
            placeholder="Search people…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </ComboboxInputGroup>
        <ComboboxList>
          {isOverride ? (
            <ComboboxItem value={USE_WORKFLOW}>
              <span className="flex min-w-0 items-center gap-2.5">
                <RotateCcwIcon
                  aria-hidden="true"
                  className="text-muted-foreground size-4 shrink-0"
                  strokeWidth={1.75}
                />
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="truncate">Use the workflow’s</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {workflowAssignee?.display_name ?? 'No one'}
                  </span>
                </span>
              </span>
            </ComboboxItem>
          ) : null}
          {found.map((person) => (
            <ComboboxItem key={person.id} value={person.id}>
              <span className="flex min-w-0 items-center gap-2.5">
                <UserAvatar
                  id={person.id}
                  displayName={person.display_name}
                  email={person.email}
                  className="size-6"
                />
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="truncate">{person.display_name}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {person.email}
                  </span>
                </span>
              </span>
            </ComboboxItem>
          ))}
        </ComboboxList>
        {found.length === 0 ? (
          <ComboboxEmpty>
            <span className="text-muted-foreground text-sm">
              {peopleLoading
                ? 'Loading people…'
                : directoryUnavailable
                  ? 'The list of people could not be loaded. Whoever is assigned stays assigned; try again shortly.'
                  : query.trim()
                    ? `No one matches “${query.trim()}”. Try part of a name or address.`
                    : 'No one can be assigned yet.'}
            </span>
          </ComboboxEmpty>
        ) : null}
      </ComboboxContent>
    </Combobox>
  );
}
